import React, { useState, useEffect } from 'react';
import { getShareholders } from '../data/capTableDatabase';
import { Shareholder } from '../data/capTableDatabase';
import { formatDate } from '../utils/dateUtils';
import { formatNumber } from '../utils/formatUtils';
import { Users, Coins, Building, TrendingUp, Search } from 'lucide-react';
import Spinner from './Spinner';

const HOASCapTable: React.FC = () => {
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [hohShareholders, setHohShareholders] = useState<Shareholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadShareholders();
  }, []);

  const loadShareholders = async () => {
    try {
      setLoading(true);
      const allShareholders = await getShareholders();
      const hoasShareholders = allShareholders.filter(s => s.entity === 'HOAS');
      const hohShareholdersList = allShareholders.filter(s => s.entity === 'HOH');
      setShareholders(hoasShareholders);
      setHohShareholders(hohShareholdersList);
    } catch (err) {
      setError('Failed to load shareholders data');
      console.error('Error loading HOAS shareholders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const totalShares = shareholders.reduce((sum, s) => sum + (s.shares || 0), 0);
  const largestShareholder = shareholders.length > 0 
    ? shareholders.reduce((max, s) => (s.shares || 0) > (max.shares || 0) ? s : max, shareholders[0])
    : null;
  const averageShares = shareholders.length > 0 ? totalShares / shareholders.length : 0;

  // Calculate cross-company ownership (HOAS ownership in HOH)
  const hohTotalShares = hohShareholders.reduce((sum, s) => sum + s.shares, 0);
  // Find HotelOnline AS ownership in Hotel Online Holding Pte. Ltd.
  const hoasInHoh = hohShareholders.find(s => s.shareholderName.toLowerCase().includes('hotelonline as') || s.shareholderName.toLowerCase().includes('hoteronline as'));
  const hoasOwnershipInHoh = hoasInHoh && hohTotalShares > 0 ? (hoasInHoh.shares / hohTotalShares) * 100 : 0;

  // Filter shareholders based on search
  const filteredShareholders = shareholders.filter(shareholder =>
    shareholder.shareholderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shareholder.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate ownership percentage (local HOAS ownership)
  const getLocalOwnershipPercentage = (shares: number) => {
    return totalShares > 0 ? ((shares / totalShares) * 100).toFixed(2) : '0.00';
  };

  // Calculate ultimate ownership percentage in HOH through HOAS
  const getUltimateOwnershipPercentage = (shares: number) => {
    if (totalShares > 0 && hoasOwnershipInHoh > 0) {
      const localPercent = (shares / totalShares) * 100;
      const ultimatePercent = (localPercent * hoasOwnershipInHoh) / 100;
      return ultimatePercent.toFixed(4);
    }
    return '0.0000';
  };

  if (loading) {
    return <Spinner size="medium" fullScreen={false} />;
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-8 text-center">
        <div className="text-red-600 mb-4 font-medium">{error}</div>
        <button
          onClick={loadShareholders}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      {shareholders.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Shareholders</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{shareholders.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Shares</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatNumber(totalShares)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {hoasOwnershipInHoh.toFixed(2)}% of HOH ownership
                </p>
              </div>
              <Coins className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Largest Holder</p>
                <p className="text-lg font-bold text-slate-900 mt-1 truncate">
                  {largestShareholder?.shareholderName || 'N/A'}
                </p>
                <p className="text-sm text-slate-500">
                  {largestShareholder ? `${getUltimateOwnershipPercentage(largestShareholder.shares || 0)}% of HOH` : ''}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-500" />
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      {shareholders.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search shareholders by name or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      {shareholders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md border border-slate-200 text-center py-16">
          <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <div className="text-slate-500 text-lg mb-2">No shareholders found</div>
          <div className="text-slate-400">Shareholders will appear here once added to the system.</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center">
              <span className="text-xl mr-3">🇳🇴</span>
              HotelOnline AS - Shareholders
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Showing {filteredShareholders.length} of {shareholders.length} shareholders
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Shareholder
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Since
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Shares
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Ownership
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredShareholders.map((shareholder, index) => {
                  const localOwnershipPercent = parseFloat(getLocalOwnershipPercentage(shareholder.shares || 0));
                  const ultimateOwnershipPercent = parseFloat(getUltimateOwnershipPercentage(shareholder.shares || 0));
                  return (
                    <tr 
                      key={shareholder.id} 
                      className={`hover:bg-slate-50 transition-colors duration-150 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-25'
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-semibold text-slate-900">
                            {shareholder.shareholderName}
                          </div>
                          {localOwnershipPercent >= 10 && (
                            <div className="text-xs text-blue-600 font-medium">Major Shareholder</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {shareholder.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          shareholder.companyIndividual === 'Company' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {shareholder.companyIndividual}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {shareholder.shareholderSince ? formatDate(shareholder.shareholderSince) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-bold text-slate-900">
                          {formatNumber(shareholder.shares || 0)}
                        </div>
                      </td>
                                              <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(localOwnershipPercent, 100)}%` }}
                              ></div>
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="text-sm font-medium text-slate-900">
                                {ultimateOwnershipPercent}%
                              </span>
                              <span className="text-xs text-slate-500">
                                of HOH
                              </span>
                            </div>
                          </div>
                        </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Enhanced Summary */}
      {shareholders.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Summary Statistics</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold text-slate-900">{shareholders.length}</div>
              <div className="text-sm text-slate-600">Total Shareholders</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold text-slate-900">{formatNumber(totalShares)}</div>
              <div className="text-sm text-slate-600">Total Shares Outstanding</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold text-slate-900">{formatNumber(Math.round(averageShares))}</div>
              <div className="text-sm text-slate-600">Average Shareholding</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HOASCapTable; 